// src/ProBookingPage.jsx
//
// Public Pro booking flow, reached at /pro/book/:slug.
//
// Flow (6 steps):
//   1. Service selection  — standalone services + packages
//   2. Stylist picker     — filtered to stylists who can do all selected services
//                           "Any available" option assigns at booking time
//   3. Date & time        — availability filtered by chosen stylist (or any)
//   4. Your details       — name, phone, email + patch test check
//   5. Payment            — Stripe deposit (if required) or confirm screen
//   6. Confirmed          — success state
//
// Deposit semantics mirror Solo (a.ii):
//   - Packages with deposit_override contribute that flat amount.
//   - Standalones + non-override packages form one bucket; account rule applies once.
//
// API base: /api/pro-booking/public/*

import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import './BookingPage.css';

const stripePromise = loadStripe(
  process.env.REACT_APP_STRIPE_CONNECT_PUBLISHABLE_KEY || ''
);

const API = (path) => `/api/pro-booking/public${path}`;

const formatPrice = (n) =>
  new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(
    Number(n || 0)
  );

const weekdayShort = (date) =>
  date.toLocaleDateString('en-GB', { weekday: 'short' });

const toLocalISODate = (d) => {
  const y  = d.getFullYear();
  const m  = String(d.getMonth() + 1).padStart(2, '0');
  const dy = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dy}`;
};

const totalDurationMins = (services) =>
  services.reduce((sum, s) => sum + (s.duration || 0) + (s.processing_time || 0), 0);

const totalPrice = (services) =>
  services.reduce((sum, s) => sum + Number(s.price || 0), 0);

const formatDuration = (mins) => {
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m === 0 ? `${h} hr` : `${h} hr ${m} min`;
};

const DAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

const parseAllowedDays = (raw) => {
  if (!raw) return null;
  const days = String(raw)
    .split(',')
    .map((d) => d.trim().toLowerCase())
    .filter(Boolean);
  return days.length ? new Set(days) : null;
};

const allowedDaysIntersection = (services) => {
  let intersection = null;
  for (const s of services) {
    const days = parseAllowedDays(s.allowed_days);
    if (days === null) continue;
    intersection =
      intersection === null
        ? new Set(days)
        : new Set([...intersection].filter((d) => days.has(d)));
  }
  return intersection;
};

const isDateAllowed = (date, intersection) => {
  if (intersection === null) return true;
  const idx = (date.getDay() + 6) % 7;
  return intersection.has(DAY_KEYS[idx]);
};

// ── Package helpers ────────────────────────────────────────────────────────

const getSlotSelection = (slot, selection) => {
  if (!slot.options.length) return null;
  const picked = selection?.slot_selections?.[slot.id];
  if (picked != null) return slot.options.find((o) => o.service_id === picked) || null;
  if (slot.slot_type === 'fixed') return slot.options[0];
  return null;
};

const isPackageComplete = (pkg, selection) => {
  if (!selection) return false;
  return pkg.slots.every((slot) => getSlotSelection(slot, selection) != null);
};

const expandPackageServices = (packages, selectedPackages) => {
  const out = [];
  for (const sel of selectedPackages) {
    const pkg = packages.find((p) => p.id === sel.package_id);
    if (!pkg) continue;
    for (const slot of pkg.slots) {
      const opt = getSlotSelection(slot, sel);
      if (!opt) continue;
      out.push({
        id:                  opt.service_id,
        name:                opt.service_name,
        duration:            opt.duration,
        processing_time:     opt.processing_time,
        price:               opt.price,
        requires_patch_test: opt.requires_patch_test,
        allowed_days:        opt.allowed_days,
      });
    }
  }
  return out;
};

const packageEffectivePrice = (pkg, selection) => {
  if (pkg.price_override != null) return pkg.price_override;
  let total = 0;
  for (const slot of pkg.slots) {
    const picked = getSlotSelection(slot, selection);
    if (picked) {
      total += Number(picked.price || 0);
    } else {
      total += Math.min(...slot.options.map((o) => Number(o.price || 0)));
    }
  }
  return Math.round(total * 100) / 100;
};

const calcDeposit = (page, standaloneServices, packages, selectedPackages) => {
  if (!page || page.deposit_type === 'none') return 0;
  let overrideTotal = 0;
  let nonOverrideTotal = 0;
  for (const sel of selectedPackages) {
    const pkg = packages.find((p) => p.id === sel.package_id);
    if (!pkg) continue;
    if (pkg.deposit_override != null) {
      overrideTotal += Number(pkg.deposit_override);
    } else {
      nonOverrideTotal += packageEffectivePrice(pkg, sel);
    }
  }
  const standaloneTotal = standaloneServices.reduce(
    (s, sv) => s + Number(sv.price || 0),
    0
  );
  const ruleBase = standaloneTotal + nonOverrideTotal;
  let ruleAmount = 0;
  if (ruleBase > 0) {
    if (page.deposit_type === 'fixed') {
      ruleAmount = Number(page.deposit_value || 0);
    } else {
      ruleAmount = Math.round(ruleBase * (page.deposit_value / 100) * 100) / 100;
    }
  }
  return Math.round((overrideTotal + ruleAmount) * 100) / 100;
};

// ── Step 1: Service selection (reused from Solo) ───────────────────────────

function ServiceStep({
  services,
  packages,
  selectedServices,
  selectedPackages,
  onToggleService,
  onTogglePackage,
  onSlotPick,
}) {
  const hasAny = (services?.length || 0) + (packages?.length || 0) > 0;
  if (!hasAny) return <p className="bp-empty">No services available yet.</p>;

  const isServiceSelected = (s) => selectedServices.some((x) => x.id === s.id);
  const getSelection      = (pkg) => selectedPackages.find((p) => p.package_id === pkg.id);

  return (
    <div className="bp-service-list">
      {packages.map((pkg) => {
        const selection = getSelection(pkg);
        const selected  = !!selection;
        const priceValue = selected
          ? packageEffectivePrice(pkg, selection)
          : pkg.effective_price;
        const priceLabel =
          pkg.price_is_from && !selected
            ? `from ${formatPrice(priceValue)}`
            : formatPrice(priceValue);

        return (
          <div
            key={`pkg-${pkg.id}`}
            className={`bp-service-card bp-package-card ${selected ? 'selected' : ''}`}
          >
            <button
              type="button"
              className="bp-package-header"
              onClick={() => onTogglePackage(pkg)}
              aria-pressed={selected}
            >
              <div className="bp-service-row">
                <span className="bp-service-name">
                  {selected ? '✓ ' : ''}
                  {pkg.name}
                  <span className="bp-package-tag">Package</span>
                </span>
                <span className="bp-service-price">{priceLabel}</span>
              </div>
              {pkg.description && (
                <div className="bp-package-desc">{pkg.description}</div>
              )}
            </button>

            {selected && (
              <div className="bp-package-slots">
                {pkg.slots.map((slot) => {
                  if (slot.slot_type === 'fixed') {
                    const opt = slot.options[0];
                    return (
                      <div key={slot.id} className="bp-slot-fixed">
                        <span className="bp-slot-label">{slot.display_label}:</span>{' '}
                        <span>{opt.service_name}</span>
                        {opt.requires_patch_test && (
                          <span className="bp-badge-patch">Patch test required</span>
                        )}
                      </div>
                    );
                  }
                  const picked = selection.slot_selections[slot.id];
                  return (
                    <fieldset key={slot.id} className="bp-slot-choice">
                      <legend className="bp-slot-label">{slot.display_label}</legend>
                      {slot.options.map((opt) => {
                        const id      = `slot-${slot.id}-opt-${opt.service_id}`;
                        const checked = picked === opt.service_id;
                        return (
                          <label
                            key={opt.service_id}
                            htmlFor={id}
                            className={`bp-slot-option ${checked ? 'selected' : ''}`}
                          >
                            <input
                              type="radio"
                              id={id}
                              name={`slot-${slot.id}`}
                              checked={checked}
                              onChange={() => onSlotPick(pkg.id, slot.id, opt.service_id)}
                            />
                            <span className="bp-slot-option-name">
                              {opt.service_name}
                              {opt.requires_patch_test && (
                                <span className="bp-badge-patch">Patch test required</span>
                              )}
                            </span>
                            <span className="bp-slot-option-price">{formatPrice(opt.price)}</span>
                          </label>
                        );
                      })}
                    </fieldset>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}

      {services.map((s) => {
        const sel = isServiceSelected(s);
        return (
          <button
            key={`svc-${s.id}`}
            type="button"
            className={`bp-service-card ${sel ? 'selected' : ''}`}
            onClick={() => onToggleService(s)}
            aria-pressed={sel}
          >
            <div className="bp-service-row">
              <span className="bp-service-name">{sel ? '✓ ' : ''}{s.name}</span>
              <span className="bp-service-price">{formatPrice(s.price)}</span>
            </div>
            <div className="bp-service-meta">
              <span>
                {s.duration} min
                {s.processing_time ? ` + ${s.processing_time} min processing` : ''}
              </span>
              {s.requires_patch_test && (
                <span className="bp-badge-patch">Patch test required</span>
              )}
            </div>
          </button>
        );
      })}
    </div>
  );
}

// ── Step 2: Stylist picker ─────────────────────────────────────────────────

function StylistStep({ stylists, selectedStylistId, onSelect }) {
  if (!stylists.length) {
    return <p className="bp-empty">No stylists available for the selected services.</p>;
  }

  return (
    <div className="bp-stylist-list">
      {/* Any available option */}
      <button
        type="button"
        className={`bp-stylist-card ${selectedStylistId === null ? 'selected' : ''}`}
        onClick={() => onSelect(null)}
        aria-pressed={selectedStylistId === null}
      >
        <div className="bp-stylist-avatar bp-stylist-avatar--any">✦</div>
        <div className="bp-stylist-info">
          <span className="bp-stylist-name">
            {selectedStylistId === null ? '✓ ' : ''}Any available stylist
          </span>
          <span className="bp-stylist-bio">
            We'll assign the first available stylist for your chosen time
          </span>
        </div>
      </button>

      {stylists.map((stylist) => {
        const selected = selectedStylistId === stylist.id;
        return (
          <button
            key={stylist.id}
            type="button"
            className={`bp-stylist-card ${selected ? 'selected' : ''}`}
            onClick={() => onSelect(stylist.id)}
            aria-pressed={selected}
          >
            {stylist.photo ? (
              <img
                src={stylist.photo}
                alt={stylist.name}
                className="bp-stylist-avatar bp-stylist-avatar--photo"
              />
            ) : (
              <div className="bp-stylist-avatar bp-stylist-avatar--initials">
                {stylist.name.charAt(0).toUpperCase()}
              </div>
            )}
            <div className="bp-stylist-info">
              <span className="bp-stylist-name">
                {selected ? '✓ ' : ''}{stylist.name}
              </span>
              {stylist.bio && (
                <span className="bp-stylist-bio">{stylist.bio}</span>
              )}
            </div>
          </button>
        );
      })}
    </div>
  );
}

// ── Booking summary (shown on steps 4, 5, 6) ──────────────────────────────

function BookingSummary({ standaloneServices, packages, selectedPackages, deposit }) {
  const totalDur = totalDurationMins([
    ...standaloneServices,
    ...expandPackageServices(packages, selectedPackages),
  ]);
  const totalPackagePrice = selectedPackages.reduce((sum, sel) => {
    const pkg = packages.find((p) => p.id === sel.package_id);
    return pkg ? sum + packageEffectivePrice(pkg, sel) : sum;
  }, 0);
  const grandTotal = totalPackagePrice + totalPrice(standaloneServices);
  const showTotal =
    standaloneServices.length + selectedPackages.length > 1 ||
    (selectedPackages.length === 1 && standaloneServices.length >= 1);

  return (
    <div className="bp-services-summary" style={{ marginBottom: 12 }}>
      {selectedPackages.map((sel) => {
        const pkg   = packages.find((p) => p.id === sel.package_id);
        if (!pkg) return null;
        const items = expandPackageServices(packages, [sel]);
        return (
          <div key={`pkg-${sel.package_id}`} className="bp-summary-package">
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontWeight: 600 }}>
              <span>{pkg.name}</span>
              <span>{formatPrice(packageEffectivePrice(pkg, sel))}</span>
            </div>
            {items.map((svc, i) => (
              <div
                key={`${sel.package_id}-${svc.id}-${i}`}
                style={{ display: 'flex', justifyContent: 'space-between', gap: 12, paddingLeft: 16, opacity: 0.7, fontSize: '0.9em' }}
              >
                <span>· {svc.name}</span>
              </div>
            ))}
          </div>
        );
      })}

      {standaloneServices.map((s, i) => (
        <div key={`svc-${s.id}-${i}`} style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
          <span>{s.name}</span>
          <span>{formatPrice(s.price)}</span>
        </div>
      ))}

      {showTotal && (
        <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid rgba(0,0,0,0.08)', marginTop: 6, paddingTop: 6, fontWeight: 600 }}>
          <span>Total</span>
          <span>{formatPrice(grandTotal)}</span>
        </div>
      )}

      <div style={{ opacity: 0.7, fontSize: '0.9em', marginTop: 4 }}>
        {formatDuration(totalDur)} total
      </div>

      {deposit > 0 && (
        <div style={{ marginTop: 6 }}>Deposit today: {formatPrice(deposit)}</div>
      )}
    </div>
  );
}

// ── Step 3: Date & time ────────────────────────────────────────────────────

function DateTimeStep({ slug, expandedServices, allowedIntersection, advanceDays, selectedStylistId, onPick }) {
  const [selectedDate, setSelectedDate] = useState(null);
  const [slots,        setSlots]        = useState([]);
  const [loading,      setLoading]      = useState(false);

  const serviceIdsKey = useMemo(
    () => expandedServices.map((s) => s.id).join(','),
    [expandedServices]
  );

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const dateRange = useMemo(() => {
    const dates = [];
    for (let i = 0; i < Math.min(advanceDays || 60, 28); i++) {
      const d = new Date(today);
      d.setDate(today.getDate() + i);
      dates.push(d);
    }
    return dates;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advanceDays]);

  useEffect(() => {
    if (!selectedDate || !serviceIdsKey) return;
    setLoading(true);
    const iso    = toLocalISODate(selectedDate);
    const params = new URLSearchParams({ date: iso, service_ids: serviceIdsKey });
    if (selectedStylistId) params.set('stylist_id', selectedStylistId);

    fetch(API(`/availability/${slug}?${params}`))
      .then((r) => r.json())
      .then((data) => setSlots(data.slots || []))
      .catch(() => setSlots([]))
      .finally(() => setLoading(false));
  }, [selectedDate, serviceIdsKey, slug, selectedStylistId]);

  return (
    <div>
      <label className="bp-label">Pick a date</label>
      <div className="bp-date-row">
        {dateRange.map((d) => {
          const iso   = toLocalISODate(d);
          const isSel = selectedDate && toLocalISODate(selectedDate) === iso;
          const allowed = isDateAllowed(d, allowedIntersection);
          return (
            <button
              key={iso}
              type="button"
              className={`bp-date-chip ${isSel ? 'selected' : ''} ${allowed ? '' : 'disabled'}`}
              onClick={() => allowed && setSelectedDate(d)}
              disabled={!allowed}
              title={allowed ? undefined : 'Not available for the selected services'}
              aria-disabled={!allowed}
            >
              <span className="bp-date-wd">{weekdayShort(d)}</span>
              <span className="bp-date-num">{d.getDate()}</span>
            </button>
          );
        })}
      </div>

      {selectedDate && (
        <>
          <label className="bp-label" style={{ marginTop: 20 }}>Pick a time</label>
          {loading ? (
            <p className="bp-muted">Checking availability…</p>
          ) : slots.length === 0 ? (
            <p className="bp-muted">No times available on this day.</p>
          ) : (
            <div className="bp-slot-grid">
              {slots.map((t) => (
                <button
                  key={t}
                  type="button"
                  className="bp-slot"
                  onClick={() => onPick(toLocalISODate(selectedDate), t)}
                >
                  {t}
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Step 4: Client details ─────────────────────────────────────────────────

function DetailsStep({ slug, expandedServices, onNext, onBack }) {
  const [form, setForm] = useState({ client_name: '', client_phone: '', client_email: '' });
  const [error,     setError]     = useState('');
  const [ptCheck,   setPtCheck]   = useState(null);
  const [ptLoading, setPtLoading] = useState(false);

  const anyNeedsPatchTest = expandedServices.some((s) => s.requires_patch_test);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
    if (name === 'client_phone' && ptCheck) setPtCheck(null);
  };

  const runPatchTestCheck = async () => {
    if (!anyNeedsPatchTest) return;
    const phone = form.client_phone.trim();
    if (phone.replace(/\D/g, '').length < 7) return;
    setPtLoading(true);
    try {
      const res = await fetch(API('/patch-test-check'), {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          slug,
          client_phone: phone,
          service_ids:  expandedServices.map((s) => s.id),
        }),
      });
      if (res.ok) setPtCheck(await res.json());
    } catch {
      // fail open
    } finally {
      setPtLoading(false);
    }
  };

  const submit = () => {
    if (!form.client_name.trim())  return setError('Please enter your name');
    if (!form.client_phone.trim()) return setError('Please enter a phone number');
    if (ptCheck?.action === 'block') {
      return setError("This booking can't be completed online. Please contact the salon directly.");
    }
    setError('');
    onNext(form, ptCheck);
  };

  const blocked     = ptCheck?.action === 'block';
  const provisional = ptCheck?.action === 'provisional';

  const patchServiceNames = expandedServices
    .filter((s) => s.requires_patch_test)
    .map((s) => s.name);

  return (
    <div className="bp-form">
      <label className="bp-label">Your name</label>
      <input
        className="bp-input"
        name="client_name"
        value={form.client_name}
        onChange={handleChange}
        autoComplete="name"
      />

      <label className="bp-label">Mobile number</label>
      <input
        className="bp-input"
        name="client_phone"
        value={form.client_phone}
        onChange={handleChange}
        onBlur={runPatchTestCheck}
        autoComplete="tel"
        inputMode="tel"
      />
      {ptLoading && <p className="bp-muted">Checking…</p>}

      <label className="bp-label">Email (optional)</label>
      <input
        className="bp-input"
        name="client_email"
        value={form.client_email}
        onChange={handleChange}
        autoComplete="email"
        type="email"
      />

      {blocked && (
        <div className="bp-block">
          <strong>We can't complete this booking online</strong>
          <p>{ptCheck.message}</p>
        </div>
      )}

      {provisional && (
        <div className="bp-warn">
          <strong>Patch test needed</strong>
          <p>
            {patchServiceNames.length > 1
              ? `${patchServiceNames.join(' and ')} need a patch test at least 48 hours before your appointment.`
              : patchServiceNames.length === 1
              ? `${patchServiceNames[0]} needs a patch test at least 48 hours before your appointment.`
              : 'This booking needs a patch test at least 48 hours before your appointment.'}{' '}
            Continue to confirm your booking — then call or pop in soon to arrange
            your patch test. If no test has been done by 48 hours before your
            appointment, the booking will be automatically cancelled.
          </p>
        </div>
      )}

      {error && <p className="bp-error">{error}</p>}

      <div className="bp-actions">
        <button type="button" className="bp-btn-secondary" onClick={onBack}>Back</button>
        <button
          type="button"
          className="bp-btn-primary"
          onClick={submit}
          disabled={blocked || ptLoading}
        >
          Continue
        </button>
      </div>
    </div>
  );
}

// ── Step 5: Stripe payment form ────────────────────────────────────────────

function PaymentInner({ clientSecret, onSuccess, amount, provisional }) {
  const stripe   = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [error,      setError]      = useState('');

  const handlePay = async () => {
    if (!stripe || !elements) return;
    setSubmitting(true);
    setError('');
    const { error: stripeError, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: window.location.href },
      redirect: 'if_required',
    });
    if (stripeError) {
      setError(stripeError.message || 'Payment failed');
      setSubmitting(false);
      return;
    }
    if (paymentIntent?.status === 'succeeded') {
      onSuccess();
    }
    setSubmitting(false);
  };

  return (
    <div className="bp-payment">
      <PaymentElement />
      {error && <p className="bp-error">{error}</p>}
      {provisional && (
        <p className="bp-muted bp-deposit-note">
          Your booking will be confirmed once payment is complete. If no patch
          test has been done by 48 hours before your appointment, the booking
          will be automatically cancelled and the deposit is non-refundable.
        </p>
      )}
      <button
        type="button"
        className="bp-btn-primary bp-btn-pay"
        onClick={handlePay}
        disabled={!stripe || submitting}
      >
        {submitting ? 'Processing…' : `Pay ${formatPrice(amount)} deposit`}
      </button>
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────

export default function ProBookingPage() {
  const { slug } = useParams();

  const [page,           setPage]           = useState(null);
  const [loadError,      setLoadError]      = useState('');
  const [step,           setStep]           = useState(1);

  // Step 1 — service selection
  const [selectedServices, setSelectedServices] = useState([]);
  const [selectedPackages, setSelectedPackages] = useState([]);

  // Step 2 — stylist (null = any available)
  const [selectedStylistId,    setSelectedStylistId]    = useState(null);
  const [assignedStylist,      setAssignedStylist]      = useState('');
  const [eligibleStylists,     setEligibleStylists]     = useState([]);
  const [stylistsLoading,      setStylistsLoading]      = useState(false);
  // Tier-adjusted prices for standalone services after a specific stylist is chosen.
  // null means "no stylist chosen yet / any available" — fall back to selectedServices prices.
  const [tierAdjustedServices, setTierAdjustedServices] = useState(null);

  // Step 3 — date & time
  const [date, setDate] = useState(null);
  const [time, setTime] = useState(null);

  // Steps 4–6
  // eslint-disable-next-line no-unused-vars
  const [details,        setDetails]        = useState(null);
  const [clientSecret,   setClientSecret]   = useState(null);
  const [depositAmount,  setDepositAmount]  = useState(0);
  const [connectAccount, setConnectAccount] = useState(null);
  const [confirmed,      setConfirmed]      = useState(null);
  const [working,        setWorking]        = useState(false);
  const [error,          setError]          = useState('');
  const [provisional,    setProvisional]    = useState(false);

  // Load page data
  useEffect(() => {
    fetch(API(`/page/${slug}`))
      .then((r) => {
        if (!r.ok) throw new Error();
        return r.json();
      })
      .then((data) => {
        setPage({
          ...data,
          services: data.services || [],
          packages: data.packages || [],
          stylists: data.stylists || [],
        });
      })
      .catch(() => setLoadError("This booking page isn't available right now."));
  }, [slug]);

  const packages    = page?.packages  || [];
  const services    = page?.services  || [];
  const allStylists = page?.stylists  || [];

  // Derived expanded service list — always from selectedServices (not tier-adjusted)
  // so that duration, patch test, and allowed_days are never affected by tier pricing.
  const packageExpandedServices = useMemo(
    () => expandPackageServices(packages, selectedPackages),
    [packages, selectedPackages]
  );
  const expandedServices = useMemo(
    () => [...packageExpandedServices, ...selectedServices],
    [packageExpandedServices, selectedServices]
  );
  const allowedIntersection = allowedDaysIntersection(expandedServices);

  // The standalone services actually used for price display and deposit calculation.
  // When a specific stylist is selected and tier prices have loaded, use those;
  // otherwise fall back to the original page prices on selectedServices.
  const effectiveStandaloneServices = tierAdjustedServices ?? selectedServices;

  // Clear date if it becomes disallowed after service change
  useEffect(() => {
    if (!date) return;
    const d = new Date(date + 'T00:00:00');
    if (!isDateAllowed(d, allowedIntersection)) {
      setDate(null);
      setTime(null);
    }
  }, [expandedServices, date, allowedIntersection]);

  // When moving to step 2, filter stylists to those who can do all selected services.
  const loadEligibleStylists = async () => {
    const allServiceIds = [...new Set(expandedServices.map((s) => s.id))];
    const filtered = allStylists.filter((stylist) =>
      allServiceIds.every((id) => stylist.service_ids.includes(id))
    );
    setEligibleStylists(filtered);
    if (selectedStylistId !== null) {
      const stillEligible = filtered.some((s) => s.id === selectedStylistId);
      if (!stillEligible) setSelectedStylistId(null);
    }
  };

  // After a specific stylist is picked, fetch tier-correct prices for standalone services.
  // Clears tier prices when "Any available" (stylistId = null) is chosen.
  const loadTierPrices = async (stylistId) => {
    if (!stylistId) {
      setTierAdjustedServices(null);
      return;
    }
    const serviceIds = selectedServices.map((s) => s.id);
    if (!serviceIds.length) {
      setTierAdjustedServices(null);
      return;
    }
    try {
      const params = new URLSearchParams({
        stylist_id:  stylistId,
        service_ids: serviceIds.join(','),
      });
      const res = await fetch(API(`/stylist-prices/${slug}?${params}`));
      if (!res.ok) return;
      const data = await res.json();
      // Build a map of service_id → tier price
      const priceMap = Object.fromEntries(data.map((s) => [s.id, s.price]));
      // Overlay tier prices onto the original service objects (keep all other fields intact)
      setTierAdjustedServices(
        selectedServices.map((s) =>
          priceMap[s.id] !== undefined ? { ...s, price: priceMap[s.id] } : s
        )
      );
    } catch {
      // Fail silently — fall back to original prices
      setTierAdjustedServices(null);
    }
  };

  // Service toggles
  const toggleService = (svc) => {
    setSelectedServices((curr) => {
      const exists = curr.some((s) => s.id === svc.id);
      return exists ? curr.filter((s) => s.id !== svc.id) : [...curr, svc];
    });
    // Service list changed — clear any cached tier prices so they're re-fetched
    // if the user goes back to step 2 and reselects a stylist.
    setTierAdjustedServices(null);
  };

  const togglePackage = (pkg) => {
    setSelectedPackages((curr) => {
      const idx = curr.findIndex((p) => p.package_id === pkg.id);
      if (idx !== -1) return curr.filter((_, i) => i !== idx);
      const slot_selections = {};
      for (const slot of pkg.slots) {
        if (slot.slot_type === 'fixed') slot_selections[slot.id] = slot.options[0].service_id;
      }
      return [...curr, { package_id: pkg.id, slot_selections }];
    });
  };

  const pickSlotOption = (pkgId, slotId, serviceId) => {
    setSelectedPackages((curr) =>
      curr.map((sel) =>
        sel.package_id === pkgId
          ? { ...sel, slot_selections: { ...sel.slot_selections, [slotId]: serviceId } }
          : sel
      )
    );
  };

  // Step 1 validation
  const allPackagesComplete = selectedPackages.every((sel) => {
    const pkg = packages.find((p) => p.id === sel.package_id);
    return pkg ? isPackageComplete(pkg, sel) : false;
  });
  const hasSelection  = selectedServices.length + selectedPackages.length > 0;
  const noSharedDays  = allowedIntersection !== null && allowedIntersection.size === 0;
  const canContinueFromStep1 = hasSelection && allPackagesComplete && !noSharedDays;

  const deposit = page
    ? calcDeposit(page, effectiveStandaloneServices, packages, selectedPackages)
    : 0;

  // Advance from step 1 → step 2
  const goToStylistStep = () => {
    setStylistsLoading(true);
    loadEligibleStylists().finally(() => {
      setStylistsLoading(false);
      setStep(2);
    });
  };

  // Steps 4 → 5/6
  const goToPayment = async (formData, ptCheck) => {
    setDetails(formData);
    setError('');
    const frontendProvisional = ptCheck?.action === 'provisional';
    setProvisional(frontendProvisional);

    const payload = {
      slug,
      service_ids: selectedServices.map((s) => s.id),
      packages: selectedPackages.map((sel) => ({
        package_id:      sel.package_id,
        slot_selections: sel.slot_selections,
      })),
      stylist_id: selectedStylistId,
      date,
      time,
      ...formData,
    };

    if (page.deposit_type === 'none') {
      setWorking(true);
      try {
        const res  = await fetch(API('/no-deposit'), {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          body:    JSON.stringify(payload),
        });
        const data = await res.json();
        if (!res.ok) {
          if (data.blocked) {
            setError(data.error || "This booking can't be completed online. Please contact the salon.");
            return;
          }
          throw new Error(data.error || 'Booking failed');
        }
        setProvisional(!!data.provisional);
        setConfirmed({
          services: effectiveStandaloneServices,
          packages: selectedPackages,
          date,
          time,
          salon: page.business_name,
          stylist: (data.confirmation && data.confirmation.stylist) || (selectedStylistId
            ? allStylists.find((s) => s.id === selectedStylistId)?.name || 'Your stylist'
            : 'Your stylist'),
          smsSent: data.sms_sent,
        });
        setStep(6);
      } catch (e) {
        setError(e.message);
      } finally {
        setWorking(false);
      }
      return;
    }

    setWorking(true);
    try {
      const res  = await fetch(API('/create-intent'), {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) {
        if (data.blocked) {
          setError(data.error || "This booking can't be completed online. Please contact the salon.");
          return;
        }
        throw new Error(data.error || 'Payment setup failed');
      }
      setClientSecret(data.client_secret);
      setDepositAmount(data.amount);
      setConnectAccount(data.stripe_account_id);
      setAssignedStylist(data.stylist || '');
      setProvisional(!!data.provisional);
      setStep(5);
    } catch (e) {
      setError(e.message);
    } finally {
      setWorking(false);
    }
  };

  const handlePaymentSuccess = () => {
    setConfirmed({
      services: effectiveStandaloneServices,
      packages: selectedPackages,
      date,
      time,
      salon: page.business_name,
      stylist: assignedStylist || (selectedStylistId
        ? allStylists.find((s) => s.id === selectedStylistId)?.name || 'Your stylist'
        : 'Your stylist'),
    });
    setStep(6);
  };

  // ── Render ───────────────────────────────────────────────────────────────

  if (loadError) {
    return (
      <div className="bp-shell">
        <div className="bp-card">
          <h2>Not available</h2>
          <p>{loadError}</p>
        </div>
      </div>
    );
  }
  if (!page) {
    return (
      <div className="bp-shell">
        <p className="bp-muted">Loading…</p>
      </div>
    );
  }

  const stepLabels = [
    'Service',
    'Stylist',
    'Date & time',
    'Your details',
    page.deposit_type === 'none' ? 'Confirm' : 'Payment',
  ];

  return (
    <div className="bp-shell">
      <div className="bp-card">
        <header className="bp-header">
          <h1>{page.business_name || 'Book an appointment'}</h1>
          {page.bio && <p className="bp-bio">{page.bio}</p>}
        </header>

        {step < 6 && (
          <div className="bp-steps">
            {stepLabels.map((label, i) => (
              <div
                key={label}
                className={`bp-step-pill ${step > i + 1 ? 'done' : ''} ${step === i + 1 ? 'current' : ''}`}
              >
                <span className="bp-step-num">{i + 1}</span>
                <span>{label}</span>
              </div>
            ))}
          </div>
        )}

        {/* ── Step 1: Services ── */}
        {step === 1 && (
          <>
            <ServiceStep
              services={services}
              packages={packages}
              selectedServices={selectedServices}
              selectedPackages={selectedPackages}
              onToggleService={toggleService}
              onTogglePackage={togglePackage}
              onSlotPick={pickSlotOption}
            />
            {hasSelection && (
              <div
                style={{
                  marginTop: 16, padding: 12,
                  background: 'rgba(0,0,0,0.04)', borderRadius: 8,
                  display: 'flex', justifyContent: 'space-between',
                  alignItems: 'center', flexWrap: 'wrap', gap: 8,
                }}
                aria-live="polite"
              >
                <span>
                  {(() => {
                    const parts = [];
                    if (selectedPackages.length)
                      parts.push(`${selectedPackages.length} package${selectedPackages.length > 1 ? 's' : ''}`);
                    if (selectedServices.length)
                      parts.push(`${selectedServices.length} service${selectedServices.length > 1 ? 's' : ''}`);
                    return parts.join(' + ');
                  })()}
                  {expandedServices.length > 0 && (
                    <> · {formatDuration(totalDurationMins(expandedServices))}</>
                  )}
                </span>
              </div>
            )}
            {!allPackagesComplete && selectedPackages.length > 0 && (
              <p className="bp-muted" style={{ marginTop: 8 }}>
                Please choose an option in each section to continue.
              </p>
            )}
            {noSharedDays && (
              <div className="bp-warn" style={{ marginTop: 12 }}>
                These services aren't offered on the same days — please pick a different combination.
              </div>
            )}
            <div className="bp-actions">
              <span />
              <button
                type="button"
                className="bp-btn-primary"
                onClick={goToStylistStep}
                disabled={!canContinueFromStep1 || stylistsLoading}
              >
                {stylistsLoading ? 'Loading…' : 'Continue'}
              </button>
            </div>
          </>
        )}

        {/* ── Step 2: Stylist ── */}
        {step === 2 && (
          <>
            <StylistStep
              stylists={eligibleStylists}
              selectedStylistId={selectedStylistId}
              onSelect={(id) => {
                setSelectedStylistId(id);
                // Clear date/time when stylist changes — availability may differ
                setDate(null);
                setTime(null);
                // Fetch tier-correct prices for this stylist (or clear if "any")
                loadTierPrices(id);
              }}
            />
            <div className="bp-actions">
              <button type="button" className="bp-btn-secondary" onClick={() => setStep(1)}>
                Back
              </button>
              <button
                type="button"
                className="bp-btn-primary"
                onClick={() => setStep(3)}
              >
                Continue
              </button>
            </div>
          </>
        )}

        {/* ── Step 3: Date & time ── */}
        {step === 3 && (
          <>
            {selectedStylistId !== null && (
              <p className="bp-muted" style={{ marginBottom: 12 }}>
                Showing availability for{' '}
                <strong>{allStylists.find((s) => s.id === selectedStylistId)?.name}</strong>
              </p>
            )}
            <DateTimeStep
              slug={slug}
              expandedServices={expandedServices}
              allowedIntersection={allowedIntersection}
              advanceDays={page.advance_booking_days}
              selectedStylistId={selectedStylistId}
              onPick={(d, t) => {
                setDate(d);
                setTime(t);
                setStep(4);
              }}
            />
            <div className="bp-actions">
              <button type="button" className="bp-btn-secondary" onClick={() => setStep(2)}>
                Back
              </button>
              <span />
            </div>
          </>
        )}

        {/* ── Step 4: Details ── */}
        {step === 4 && (
          <>
            <div className="bp-summary">
              <BookingSummary
                standaloneServices={effectiveStandaloneServices}
                packages={packages}
                selectedPackages={selectedPackages}
                deposit={deposit}
              />
              <div>
                {new Date(date).toLocaleDateString('en-GB', {
                  weekday: 'long', day: 'numeric', month: 'long',
                })}{' '}
                at {time}
              </div>
              {selectedStylistId !== null && (
                <div>
                  with {allStylists.find((s) => s.id === selectedStylistId)?.name}
                </div>
              )}
            </div>
            <DetailsStep
              slug={slug}
              expandedServices={expandedServices}
              onBack={() => setStep(3)}
              onNext={goToPayment}
            />
            {working && <p className="bp-muted">Setting up your booking…</p>}
            {error && <p className="bp-error">{error}</p>}
          </>
        )}

        {/* ── Step 5: Stripe payment ── */}
        {step === 5 && clientSecret && (
          <Elements
            stripe={stripePromise}
            options={{
              clientSecret,
              appearance: { theme: 'stripe' },
              stripeAccount: connectAccount || undefined,
            }}
          >
            <div className="bp-summary">
              <BookingSummary
                standaloneServices={effectiveStandaloneServices}
                packages={packages}
                selectedPackages={selectedPackages}
                deposit={0}
              />
              <div>
                {new Date(date).toLocaleDateString('en-GB', {
                  weekday: 'long', day: 'numeric', month: 'long',
                })}{' '}
                at {time}
              </div>
              {selectedStylistId !== null && (
                <div>
                  with {allStylists.find((s) => s.id === selectedStylistId)?.name}
                </div>
              )}
              <div>Deposit: {formatPrice(depositAmount)}</div>
            </div>
            {provisional && (
              <div className="bp-warn">
                <strong>Patch test needed</strong>
                <p>
                  This booking needs a patch test at least 48 hours before your
                  appointment. Pay your deposit to confirm your booking, then call
                  or pop in soon to arrange your test.
                </p>
              </div>
            )}
            <PaymentInner
              clientSecret={clientSecret}
              amount={depositAmount}
              provisional={provisional}
              onSuccess={handlePaymentSuccess}
            />
            <div className="bp-actions">
              <button
                type="button"
                className="bp-btn-secondary"
                onClick={() => { setClientSecret(null); setStep(4); }}
              >
                Back
              </button>
              <span />
            </div>
          </Elements>
        )}

        {/* ── Step 6: Confirmed ── */}
        {step === 6 && confirmed && (
          <div className="bp-confirmed">
            <div className={`bp-tick ${provisional ? 'provisional' : ''}`}>
              {provisional ? '⚠' : '✓'}
            </div>
            <h2>
              {provisional ? 'Booking confirmed — patch test needed' : 'Booking confirmed'}
            </h2>
            <div className="bp-summary">
              <BookingSummary
                standaloneServices={confirmed.services}
                packages={packages}
                selectedPackages={confirmed.packages}
                deposit={0}
              />
              <div>
                {new Date(confirmed.date).toLocaleDateString('en-GB', {
                  weekday: 'long', day: 'numeric', month: 'long',
                })}{' '}
                at {confirmed.time}
              </div>
              <div>with {confirmed.stylist}</div>
              <div>{confirmed.salon}</div>
            </div>

            {provisional ? (
              <div className="bp-warn" style={{ marginTop: 16 }}>
                <strong>Your booking is confirmed — patch test needed</strong>
                <p>
                  This booking needs a patch test at least 48 hours before your
                  appointment. Please call or pop in soon to arrange one. If no
                  patch test has been done by 48 hours before your appointment,
                  the booking will be automatically cancelled.
                </p>
              </div>
            ) : (
              <p className="bp-muted">{confirmed.smsSent === false
                ? "We couldn't send a confirmation text, so please make a note of your date and time above."
                : "You'll receive a confirmation SMS shortly."}</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}